import { getE3RedirectUrl } from '@/api/Operator';

export interface ExternalRedirectConfig {
  path: string;
}

export interface ExternalRedirects {
  [routeName: string]: ExternalRedirectConfig;
}

export const EXTERNAL_REDIRECTS: ExternalRedirects = {
  'account-operator-missions': {
    path: 'missions'
  },
  'account-operator-rewards': {
    path: 'rewards'
  }
};

interface CacheEntry {
  url: string;
  timestamp: number;
  ttl: number;
}

class ExternalRedirectService {
  private cache: Record<string, CacheEntry> = {};
  private defaultCacheTTL = 5 * 60 * 1000;

  async getExternalUrl(
    path: string,
    options: {
      force?: boolean;
      ttl?: number;
      params?: Record<string, any>;
    } = {}
  ): Promise<string> {
    const { force = false, ttl = this.defaultCacheTTL, params = {} } = options;
    const cacheKey = this.generateCacheKey(path, params);

    if (!force) {
      const cachedUrl = this.getFromCache(cacheKey);
      if (cachedUrl) {
        console.log(`[ExternalRedirects] Using cached URL for path: ${path}`);
        return cachedUrl;
      }
    }

    try {
      const url = await getE3RedirectUrl(path);

      this.cache[cacheKey] = {
        url,
        timestamp: Date.now(),
        ttl
      };

      return url;
    } catch (error) {
      console.error(
        `[ExternalRedirects] Error fetching URL for path: ${path}`,
        error
      );
      throw error;
    }
  }

  clearCache(path?: string, params?: Record<string, any>): void {
    if (path) {
      const cacheKey = this.generateCacheKey(path, params);
      delete this.cache[cacheKey];
      console.log(`[ExternalRedirects] Cache cleared for path: ${path}`);
    } else {
      this.cache = {};
      console.log('[ExternalRedirects] Cache cleared for all paths');
    }
  }

  openInNewTab(url: string): Window | null {
    return window.open(url, '_blank');
  }

  private generateCacheKey(path: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return path;
    }
    return `${path}:${JSON.stringify(params)}`;
  }

  private getFromCache(cacheKey: string): string | null {
    const cacheEntry = this.cache[cacheKey];

    if (!cacheEntry) {
      return null;
    }

    const { url, timestamp, ttl } = cacheEntry;
    const isExpired = Date.now() - timestamp > ttl;

    if (isExpired) {
      delete this.cache[cacheKey];
      return null;
    }

    return url;
  }
}

export const externalRedirectService = new ExternalRedirectService();

export async function redirectToExternal(
  routeName: string,
  options: {
    force?: boolean;
    params?: Record<string, any>;
  } = {}
): Promise<Window | null> {
  const { force = false, params = {} } = options;

  const config = EXTERNAL_REDIRECTS[routeName];
  if (!config) {
    console.error(
      `[ExternalRedirects] No configuration found for route: ${routeName}`
    );
    return null;
  }

  try {
    const url = await externalRedirectService.getExternalUrl(config.path, {
      force,
      params
    });

    return externalRedirectService.openInNewTab(url);
  } catch (error) {
    console.error('[ExternalRedirects] Error during redirect:', error);
    return null;
  }
}
